/*
{
    fields: [ 'npiNumber' ],
    icon: <Component />,
    isActive: boolean,
    isValid: boolean,
    title: 'Provider',
}
 */

import { faUserMd } from "@fortawesome/free-solid-svg-icons/faUserMd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PersonIcon from '@material-ui/icons/Person';
import LocalHospitalIcon from '@material-ui/icons/LocalHospital';
import AssignmentIcon from '@material-ui/icons/Assignment';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import ListIcon from '@material-ui/icons/List';
import React from "react";
import { fetchMember, fetchEligibility, createPa } from "./api-service";
import { maxDiagnosisCodes } from "./app-constants";

const getDiagnosisCodes = () => {
  return new Array(maxDiagnosisCodes + 2)
    .fill("")
    .filter((item, i) => i)
    .map((item, i) => `diagnosisCode${i}`);
}

const PaFormSteps = [
  {
    fields: [
      ["providerName", "providerType", "npiNumber"],
      ["providerFax", "providerPhone"],
      ["providerIsPrescriber"],
      ["prescriberNPI", "prescriberFirstName", "prescriberLastName"],
      ["prescriberFax", "prescriberPhone"],
    ],
    icon: <FontAwesomeIcon icon={faUserMd} />,
    title: "PROVIDER",
  },
  {
    fields: [
      ["submitterHeader"],
      ["submitterFirstName", "submitterLastName"],
      ["submitterContactPhone", "submitterHippaSecureFax"],
    ],
    icon: <PersonIcon />,
    title: "SUBMITTER",
    onNext: async (fields, setFields) => {
        const change = {
          isCompound: { value: "no" },
          dawTrialCode: { value: "no" }
        };
        setFields(change);
        return { success: true, message: "" };
      }
  },
  {
    fields: [
      ["beneficiaryHeader"],
      ["beneficiaryFirstName", "beneficiaryLastName"],
      ["beneficiaryID", "beneficiaryDOB"],
    ],
    icon: <PersonIcon />,
    title: "MEMBER",
    onNext: async (fields, setFields) => {
      let ret = { success: false, message: "" };
      const payload = {
        firstName: fields.beneficiaryFirstName.value,
        lastName: fields.beneficiaryLastName.value,
        birthDate: fields.beneficiaryDOB.value,
        cardHolderId:
          fields.beneficiaryID.value &&
          fields.beneficiaryID.value.toUpperCase()
      };
  
      try {
        let res = await fetchMember(payload);
        const { message } = res;
        ret.message = message;
        if (message !== "" && message !== null) {
          throw new SyntaxError(message);
        }
        else {
          ret.success = true;
          let proactivePaMessage = res.errorMessage ? res.errorMessage : null
          const stateUpdate = {
            patientName: `${fields.beneficiaryFirstName.value} ${fields.beneficiaryLastName.value}`
          };
          if (proactivePaMessage !== null) {
            ret.message= proactivePaMessage
          }
          setFields(stateUpdate);
        }
      } catch (err) {
        if (err instanceof SyntaxError) {
          ret.message = err.message;
          ret.success = false;
        } else {
          console.log(err);
        }
      }
      return ret;
    },
  },
  {
    fields: [
      ["drugInfoHeader"],
      ["isCompound"],
      ["searchBy", "enteralWarning"],
      ["ndcInput"],
      ["ndcResult"],
      ["DRUG_INFO_HEADER"],
      ["drugName"],
      ["quantity"],
      ["daysSupply"],
      ["drugNotFound"],
      ["compoundDosageFormCd", "compoundDispUnitFormInd"],
      ["compounds"],
      ["dawTrialCode"]
    ],
    icon: <LocalHospitalIcon />,
    title: "DRUG INFORMATION",
    onBack: (fields, setFields) => {
      if (fields.trialAPIStatus.value === false) {
        setFields({
          trialAPIStatus: "",
          drugFormData: "",
          drugName: "",
          drugForm: "",
          drugStrength: "",
          quantity: "",
          daysSupply: "",
        });
      }
      return true;
    },
    onNext: async (fields, setFields) => {
      let ret = { success: false, message: "" };
      const payload = {
        beneficiaryFirstName: fields.beneficiaryFirstName.value,
        beneficiaryLastName: fields.beneficiaryLastName.value,
        beneficiaryDob: fields.beneficiaryDOB.value,
        npiNumber: fields.npiNumber.value, 
        npi: fields.npiNumber.value, 
        beneficiaryId:
          fields.beneficiaryID.value &&
          fields.beneficiaryID.value.toUpperCase(),
        drugLabelName: fields.drugName.value,
        daysSupply: fields.daysSupply.value,
        numberRefills: fields.numberRefills.value,
        prescriberNPI: fields.prescriberNpiSuccess.value,
        providerIsPrescriber: fields.providerIsPrescriber.value === "yes",
        providerType: fields.providerType.value,
        quantity: fields.quantity.value,
        ndc: fields.ndc.value,
        compoundDrug: fields.isCompound.value === "yes" ? true : false,
        dawTrialCode: fields.dawTrialCode.value === "yes" ? "1" : "0"
      };
      if (fields.providerType.value === "PR") {
        payload.npiNumber = null;
        payload.npi = null;
        payload.prescriberNPI = fields.npiNumber.value;
      }
      if (fields.searchBy.value === "ndc") {
        payload.searchByNDC = true;
      } else {
        payload.searchByNDC = false;
      }
      if (payload.dawTrialCode === "0") {
        const change = {
          dawSelectionCode: { value: "0", isDirty:false, options: [
              { value: "0", label: "0 - No Product Selection Ind" },
              { value: "2", label: "2 - Dispensed Patient Request" },
              { value: "3", label: "3 - Dispensed Pharmacist Selected" },
              { value: "4", label: "4 - Generic Drug Not in Stock" },
              { value: "5", label: "5 - Dispensed Brand as Generic" },
              { value: "6", label: "6 - Override" },
              { value: "7", label: "7 - No Substitue-Brand by Law" },
              { value: "8", label: "8 - Generic Drug Unavailable" },
              { value: "9", label: "9 - Other" },
            ]}
        };
        setFields(change);
      }

      try {
        let res = await fetchEligibility(payload);
        const { message } = res;
        ret.message = message;
        if (message !== "" && message !== null) {
          throw new SyntaxError(message);
        }
        else {
          ret.success = true;
          let proactivePaMessage = res.errorMessage ? res.errorMessage : null
          let finalUpdate = {};
          const stateUpdate = {
            trialAPIStatus: true,
            patientName: `${fields.beneficiaryFirstName.value} ${fields.beneficiaryLastName.value}`,
            drugNamePrefilled: fields.drugName.value,
            quantityPrefilled: fields.quantity.value,
            daysSupplyPrefilled: fields.daysSupply.value,
            ndc: res.calcNDC,
            proactivePaMessage: proactivePaMessage,
            rejectCode1: res.rejectCode1 ? res.rejectCode1 : null,
            internalErrorCodes1: res.internalErrorCodes1 ? res.internalErrorCodes1 : null,
            externalConditionCode: res.externalConditionCode ? res.externalConditionCode : null,
            internalConditionCode: res.internalConditionCode ? res.internalConditionCode : null,
            tReqId: res.tReqId ? res.tReqId : null,
            requestType: res.requestType === "New RequestTransition"
              ? {
                value: "New Request - Transition",
                isDirty: false,
                options: [
                  {
                    value: "New Request - Transition",
                    label: "New Request - Pro-Active PA - Transition"
                  }
                ]
              }
              : res.requestType === "New RequestExpiring"
                ? {
                  value: "New Request - Expiring PA",
                  isDirty: false,
                  options: [
                    {
                      value: "New Request - Expiring PA",
                      label: "New Request - Pro-Active PA - Expiring"
                    }
                  ]
                }
                : {
                  value: "New Request",
                  isDirty: false,
                  options: [
                    "New Request",
                    "Renewal",
                    "Appeal",
                    "Drug Price Review",
                  ]
                }
          };
          if (proactivePaMessage !== null) {
            ret.message= proactivePaMessage
          }
          if (fields.isCompound.value === "yes") {
            const update2 = {
              trialAPIStatus: "",
              drugFormData: "",
              drugName: "",
              drugForm: "",
              drugStrength: "",
              quantity: "",
              daysSupply: "",
              ndc: ""
            };
            finalUpdate = { ...stateUpdate, ...update2 };
          } else {
            finalUpdate = { ...stateUpdate };
          }

          setFields(finalUpdate);
        }
      } catch (err) {
        if (err instanceof SyntaxError) {
          ret.message = err.message;
          ret.success = false;
          setFields({ trialAPIStatus: false });
        } else {
          console.log(err);
        }
      }
      return ret;
    },
  },
  {
    fields: [
      ["memberInformation"],
      ["patientName"],
      ["beneficiaryLocation", "patientHeight", "patientWeight"],
      [""],
      [""],
      ["allergyRadio"],
      ["allergyInformation"],
      ["drugAndDiagnosisInformation"],
      ["drugNamePrefilled", "ndcPrefilled"],
      ["quantityPrefilled", "daysSupplyPrefilled"],
      ["doseInput", "routeInput", "frequencyInput"],
      ["compoundsDisplay"],
      ["numberRefills", "dawSelectionCode"],
      ["requestType"],
      ["requestBeginDate", "requestEndDate"],
      ["diagnosisCode0", "diagnosisCode1"],
      ["therapyRadio"],
      ["therapyDate"],
      ["therapyMedicationSelect"],
      ["therapyOtherInfo"],
      ["therapyClinicalOutcome"],
      ["trialFailure"],
      ["nonCdlMedications"],
      ["offLabelRadio"],
      ["offLabelDetails"],
      ["alternateShortageRadio"],
      ["alternateUnavailableRadio"],
      ["alternateShortageConfirmationSelect"],
      ["alternateShortageDetails"],
      ["enteralNutritionProductRadio"],
      ["enteralNdcInput", "enteralKcalInput", "enteralQuantityInput"],
      ["enteralClinicalRationalDetails"],
      ["enteralDaysSupplyInput", "enteralAdministrationSelect"],
      ["enteralRationalDetails"],
      ["enteralInfantProductRadio"],
      ["enteralInfantBirthWeightInput"],
      ["enteralInfantCurrentWeightInput"],
      ["enteralInfantLbwInput"],
      ["topicalProductRadio"],
      ["topicalApplicationDetails"],
      ["topicalAreaInput"],
      ["topicalWoundCareRadio"],
      ["topicalNecroticRadio"],
      ["additionalInformation"],
    ],
    icon: <AssignmentIcon />,
    title: "PA INFORMATION",
    onNext: async (fields, setFields) => {
      const diagnosisCodes = [
        { diagnosisCdQlfr: "02", diagnosisCd: fields.diagnosisCode0.value },
      ];
      if (fields.diagnosisCode1.value) {
        diagnosisCodes.push({
          diagnosisCdQlfr: "02",
          diagnosisCd: fields.diagnosisCode1.value,
        });
      }
      setFields({ diagnosisCodes });

      return { success: true, message: "" };
    },
  },
  {
    fields: [["attachments"]],
    icon: <AttachFileIcon />,
    title: "ATTACHMENTS",
  },
  {
    fields: [
      //Provider Tab
      ["providerName", "providerType", "npiNumber"],
      ["providerFax", "providerPhone", "prescriberFirstName"],
      ["providerIsPrescriber"],
      ["prescriberNPI", "prescriberSpecialty", "prescriberFirstName"],
      ["prescriberLastName", "prescriberFax", "prescriberPhone"],
      // Submitter Tab
      ["submitterFirstName", "submitterLastName"],
      ["submitterContactPhone", "submitterHippaSecureFax"],
      //Beneficiary/Drug Tab
      ["beneficiaryFirstName", "beneficiaryLastName"],
      ["beneficiaryID", "beneficiaryDOB"],
      ["isCompound"],
      ["drugName", "ndcPrefilled"],
      ["quantity", "daysSupply"],
      // Pa Information Tab
      ["patientName", "patientHeight", "patientWeight"],
      ["beneficiaryLocation"],
      ["numberRefills", "dawSelectionCode"],
      ["requestType"],
      ["requestBeginDate", "requestEndDate"],
      getDiagnosisCodes(),
      ["trialFailure"],
      ["additionalInformation"],
      ["enteralNdc"],
      // Attachments Tab
      ["uploadList"],
    ],
    icon: <ListIcon />,
    title: "SUMMARY",
    onNext: async (fields) => {
      const payload = {};
      const now = new Date();
      const formatted = new Intl.DateTimeFormat('en-US', {
        dateStyle: 'short',
        timeStyle: 'medium',
        timeZone: 'America/Los_Angeles'
      }).format(now)

      for (const [key, value] of Object.entries(fields)) {
        payload[key] =
          typeof value.value === "string" ? value.value.trim() : value.value;
      }

      payload.drugFormData = null;
      payload.providerIsPrescriber =
        fields.providerIsPrescriber.value === "yes";
      payload.isCompound = fields.isCompound.value === "yes";
      payload.attachments =
        fields.attachments.value.length > 0 ? fields.attachments.value : [];
      payload.additionalInformation = `${formatted} (PT): ${payload.additionalInformation}`
      if (payload.enteralNdc) {
        payload.additionalInformation = `NDC: ${payload.enteralNdc}\n${formatted} (PT): ${payload.additionalInformation}`
      }
      delete payload.enteralNdc
      const ret = {
        retry: true,
        success: true,
        message: null,
        authNumber: null,
      };
      try {
        const res = await createPa({
          createPayload: payload,
          files: payload.attachments,
        });
        if (!res) {
          ret.message = "Unknown Error";
          ret.success = false;
        }
        if (
          res.errorMessage ||
          (res.errorMsg && res.errorMsg !== "") ||
          !res.priorAuthNumber
        ) {
          ret.message = res.errorMessage || res.errorMsg;
          ret.success = false;
        } else {
          ret.authNumber = res.priorAuthNumber;
        }
      } catch (error) {
        ret.message = error.message;
        ret.success = false;
      }

      return ret;
    },
  },
];

export default PaFormSteps;
